import { useSelector } from "react-redux";
import { hasAnyPermission, ROLE_TYPES } from "../../utils/utils";
import {
  dashboard_active_icon,
  dashboard_default_icon,
  exercises_active_icon,
  exercises_default_icon,
  home_active_icon,
  home_default_icon,
  location_room_active_icon,
  location_room_default_icon,
  performance_active_icon,
  performance_default_icon,
  topic_active_icon,
  topic_default_icon,
  users_active_icon,
  users_default_icon,
} from "../../constants/icons";


const hasOnlyUserRole = (roles) => {
  return (
    !ROLE_TYPES.isHr(roles) &&
    !ROLE_TYPES.isCompetencySpoc(roles) &&
    !ROLE_TYPES.isCompetencyLead(roles) &&
    !ROLE_TYPES.isAdmin(roles) &&
    ROLE_TYPES.isUser(roles)
  );
};

const canAccessExerciseMenus = (roles, hasOnlyUser) => {
  return (
    ROLE_TYPES.isCompetencySpoc(roles) ||
    ROLE_TYPES.isHr(roles) ||
    ROLE_TYPES.isCompetencyLead(roles) ||
    ROLE_TYPES.isAdmin(roles) ||
    hasOnlyUser
  );
};

const buildSubMenus = (subMenuItems) => {
  return subMenuItems.filter((submenu) => submenu !== null);
};

const buildExercisesSubMenus = (permission) => {
  return buildSubMenus([
    permission?.attendee ? { name: "As Attendee", linkTo: "/exercises/attendee" } : null,
    permission?.exerciseReviewer || permission?.mentor
      ? { name: "As Mentor/Reviewer", linkTo: "/exercises/reviewer" }
      : null,
  ]);
};

const buildAssessmentsSubMenus = (permission) => {
  return buildSubMenus([
    permission?.attendee ? { name: "As Attendee", linkTo: "/assessmentList/attendee" } : null,
    permission?.mentor ? { name: "As Mentor", linkTo: "/assessmentList/mentor" } : null,
    permission?.assessmentInterviewer
      ? { name: "As Interviewer", linkTo: "/assessmentList/interviewer" }
      : null,
  ]);
};

const buildPerformanceSubMenus = (permission) => {
  return buildSubMenus([
    permission?.attendee ? { name: "As Attendee", linkTo: "/performance/attendee" } : null,
    permission?.mentor ? { name: "As Mentor", linkTo: "/performance/reviewer" } : null,
  ]);
};

const buildExerciseMenuItems = (permission) => {
  return [
    {
      defaultIcon: exercises_default_icon,
      hoverIcon: exercises_active_icon,
      name: "Exercises",
      linkTo: "/exercise",
      isExpandable: true,
      subMenus: buildExercisesSubMenus(permission),
    },
    {
      defaultIcon: exercises_default_icon,
      hoverIcon: exercises_active_icon,
      name: "Assessments",
      linkTo: "/assessmentList",
      isExpandable: true,
      subMenus: buildAssessmentsSubMenus(permission),
    },
    {
      defaultIcon: performance_default_icon,
      hoverIcon: performance_active_icon,
      name: "Performance",
      linkTo: "/performance",
      isExpandable: true,
      subMenus: buildPerformanceSubMenus(permission),
    },
  ];
};

const canAccessHome = (roles, permission, hasOnlyUser) => {
  return (
    ROLE_TYPES.isHr(roles) ||
    ROLE_TYPES.isCompetencySpoc(roles) ||
    ROLE_TYPES.isCompetencyLead(roles) ||
    hasAnyPermission(permission) ||
    hasOnlyUser
  );
};

const buildFormsSubMenus = (roles, permission) => {
  const isHr = ROLE_TYPES.isHr(roles);
  const isSpoc = permission?.spoc;

  return buildSubMenus([
    isHr ? { name: "Performance Dashboard", linkTo: "/performanceFeedbacks/dashboard", isDashboard: true } : null,
    (isHr || isSpoc) ? { name: "Manage", linkTo: "/performanceFeedbacks/manage" } : null,
    !isHr ? { name: "As Responder", linkTo: "/performanceFeedbacks/asResponder" } : null,
    permission?.attendee ? { name: "As Attendee", linkTo: "/performanceFeedbacks/asAttendee" } : null,
    permission?.mentor ? { name: "As Mentor", linkTo: "/performanceFeedbacks/asMentor", } : null,
  ]);
};



export const SidebarList = (roles) => {
  const { permission } = useSelector((state) => state?.security);
  const isSpoc = permission?.spoc;
  const hasOnlyUser = hasOnlyUserRole(roles);
  const canAccessExercises = canAccessExerciseMenus(roles, hasOnlyUser);

  const list = [];

  if (isSpoc) {
    list.push({
      defaultIcon: topic_default_icon,
      hoverIcon: topic_active_icon,
      name: "Topics",
      linkTo: "/topic",
      isExpandable: false,
    });
  }

  if (canAccessExercises) {
    list.push(...buildExerciseMenuItems(permission));
  }


  if (ROLE_TYPES.isAdmin(roles) || isSpoc) {
    list.push({
      defaultIcon: location_room_default_icon,
      hoverIcon: location_room_active_icon,
      name: "Location & Rooms",
      linkTo: "/locations-and-rooms",
      isExpandable: false,
    });
  }

  if (ROLE_TYPES.isCompetencySpoc(roles) || ROLE_TYPES.isAdmin(roles)) {
    list.push({
      defaultIcon: users_default_icon,
      hoverIcon: users_active_icon,
      name: "Users",
      linkTo: "/users",
      isExpandable: false,
    });
  }


  if (canAccessHome(roles, permission, hasOnlyUser)) {
    list.push({
      defaultIcon: home_default_icon,
      hoverIcon: home_active_icon,
      name: "Home",
      linkTo: "/",
      isExpandable: false,
    });
  }

  if (ROLE_TYPES.isUser(roles)) {
    list.push({
      defaultIcon: dashboard_default_icon,
      hoverIcon: dashboard_active_icon,
      name: "Dashboard",
      linkTo: "/dashboard",
      isExpandable: false,
    });
  }

  const formsSubMenus = buildFormsSubMenus(roles, permission);
  if (formsSubMenus.length > 0) {
    list.push({
      defaultIcon: dashboard_default_icon,
      hoverIcon: dashboard_active_icon,
      name: "Performance Feedbacks",
      linkTo: "/performanceFeedbacks",
      isExpandable: true,
      subMenus: formsSubMenus,
    });
  }


  const orderKeys = [
    "Home", "Dashboard", "Location & Rooms", "Topics",
    "Users", "Exercises", "Assessments", "Performance", "Performance Feedbacks"
  ];

  return orderKeys
    .map((name) => list.find((item) => item.name === name))
    .filter(Boolean);
};